import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Save } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { CourseDetailsFields } from '../../components/training/course/CourseDetailsFields';
import { CourseTeachersFields } from '../../components/training/course/CourseTeachersFields';
import { CourseCostsFields } from '../../components/training/course/CourseCostsFields';
import { CourseCostSummary } from '../../components/training/course/CourseCostSummary';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { trainingCoursesStore } from '../../services/trainingCoursesStore';
import { emptyCourseDraft, parseCourseStatus } from '../../utils/trainingCourses';
import { CourseErrors, CourseTeacher, TrainingCourseDraft } from '../../types/trainingCourse';
export function CourseEditorPage() {
  const {
    courseId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courses = useTrainingCourses();
  const existing = courseId ? courses.find(c => c.id === courseId) : undefined;
  const [draft, setDraft] = useState<TrainingCourseDraft>(() => existing ? {
    ...existing,
    teachers: [...(existing.teachers || [])]
  } : emptyCourseDraft(parseCourseStatus(searchParams.get('stato'))));
  const [errors, setErrors] = useState<CourseErrors>({});
  if (courseId && !existing) return <Navigate to="/training/corsi" replace data-id="element-6203" data-mp="33zzh5lv8uahm" />;
  const update = (changes: Partial<TrainingCourseDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...changes
    }));
    setErrors(prev => {
      const next = {
        ...prev
      };
      Object.keys(changes).forEach(k => delete next[k]);
      if ('startDate' in changes) delete next.endDate;
      return next;
    });
  };
  const updateTeachers = (teachers: CourseTeacher[]) => {
    setDraft(prev => ({
      ...prev,
      teachers
    }));
    setErrors(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith('teacher-'))));
  };
  const validate = (): CourseErrors => {
    const next: CourseErrors = {};
    const isOnDemand = draft.status === 'ondemand';
    if (!draft.productCode) next.productCode = 'Seleziona tipologia di corso e codice prodotto';
    if (!draft.courseCode.trim()) next.courseCode = 'Inserisci il codice specifico del corso';
    if (draft.characteristic === 'Trainato' && !draft.leadCourseId) next.leadCourseId = 'Seleziona il corso trainante';
    if (!isOnDemand && draft.commission === 'Cliente' && !draft.clientName) next.clientName = "Seleziona l'azienda";
    if (!isOnDemand && draft.venue === 'cliente' && !draft.clientSite) next.clientSite = 'Seleziona la sede del cliente';
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) next.endDate = 'La data di fine non può precedere la data di inizio';
    (isOnDemand ? [] : draft.teachers || []).forEach((t, i) => {
      if (!t.teacherId) next[`teacher-${i}`] = 'Seleziona il docente';
    });
    return next;
  };
  const handleSave = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    const saved = trainingCoursesStore.save({
      ...draft,
      courseCode: draft.courseCode.trim()
    });
    navigate(`/training/corsi/${saved.id}`, {
      state: {
        saved: existing ? 'updated' : 'created'
      }
    });
  };
  const cancelHref = existing ? `/training/corsi/${existing.id}` : `/training/corsi?tab=${draft.status}`;
  const errorCount = Object.keys(errors).length;
  return <Layout data-id="element-6204" data-mp="81g4z0tpftwg">
      <Link to={cancelHref} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-6205" data-mp="85dpnqvxktig">
        <ArrowLeft className="h-4 w-4" data-id="element-6206" data-mp="2sujjgebotasw" />{' '}
        {existing ? 'Dashboard corso' : 'Corsi'}
      </Link>
      <div className="mb-6" data-id="element-6207" data-mp="383x27cxuxeq3">
        <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6208" data-mp="38587jh6yaujb">
          {existing ? 'Modifica corso' : 'Nuovo corso'}
        </h1>
        {existing && <p className="text-slate-500 mt-1" data-id="element-6209" data-mp="2yedkialvrqwt">
            <span className="font-mono" data-id="element-6210" data-mp="2yazg6wtnp7mq">{existing.courseCode}</span>
            {existing.name && ` · ${existing.name}`}
          </p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-6211" data-mp="3r0yb38uj7yzb">
        <div className="lg:col-span-2 space-y-6" data-id="element-6212" data-mp="3m2enl40aif4u">
          <Card title="Dati corso" noHoverShadow data-id="element-6213" data-mp="3m3pomw2cp8s9">
            <CourseDetailsFields draft={draft} onChange={update} errors={errors} courses={courses} isEditing={Boolean(existing)} data-id="element-6214" data-mp="3i0tc9n3em8wr" />
          </Card>
          {draft.status !== 'ondemand' && <Card title="Docenti" noHoverShadow data-id="element-6215" data-mp="21n2vpgv3ls68">
              <CourseTeachersFields teachers={draft.teachers || []} onChange={updateTeachers} errors={errors} productCode={draft.productCode} data-id="element-6216" data-mp="21m9yp7lxyh1a" />
            </Card>}
          <Card title="Costi" noHoverShadow data-id="element-6217" data-mp="q5qmen6q33v1">
            <CourseCostsFields draft={draft} onChange={update} data-id="element-6218" data-mp="q4xal7swpo61" />
          </Card>
        </div>

        <aside className="lg:sticky lg:top-6 space-y-4" data-id="element-6219" data-mp="uxgwcs1qzplx">
          <CourseCostSummary course={draft} data-id="element-6220" data-mp="zvihdiiqhz01" />
          {errorCount > 0 && <div role="alert" className="flex gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-id="element-6221" data-mp="zxmixtt5ulx5">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-6222" data-mp="19tg584wolts4" />
              <p className="text-sm text-red-700" data-id="element-6223" data-mp="1dv1bh7epn9tc">
                {errorCount === 1 ? 'C’è 1 campo da correggere.' : `Ci sono ${errorCount} campi da correggere.`}
              </p>
            </div>}
          <div className="flex gap-3" data-id="element-6224" data-mp="3fn6cr9i3njgq">
            <Button variant="flat" color="default" className="flex-1" onClick={() => navigate(cancelHref)} data-id="element-6225" data-mp="ms668t3v8m7l">
              Annulla
            </Button>
            <Button className="flex-1" onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-6227" data-mp="rqr6gsj4b5w1" />} data-id="element-6226" data-mp="vtdra9hdfqjq">
              {existing ? 'Salva modifiche' : 'Crea corso'}
            </Button>
          </div>
        </aside>
      </div>
    </Layout>;
}